import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import PatientContextBar from '../../components/navigation/PatientContextBar';
import QuickActions from '../../components/navigation/QuickActions';
import SessionManager from '../../components/navigation/SessionManager';
import DocumentTemplateSelector from './components/DocumentTemplateSelector';
import RichTextEditor from './components/RichTextEditor';
import PatientReferencePanel from './components/PatientReferencePanel';
import SmartPhrasesPanel from './components/SmartPhrasesPanel';
import TreatmentDecisionSupport from './components/ClinicalDecisionSupport';
import DocumentVersionHistory from './components/DocumentVersionHistory';
import ICDCodeLookup from '../../components/icd/ICDCodeLookup';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const TreatmentDocumentation = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [documentContent, setDocumentContent] = useState('');
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [showReferencePanel, setShowReferencePanel] = useState(true);
  const [showSmartPhrases, setShowSmartPhrases] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showICDLookup, setShowICDLookup] = useState(false);
  const [selectedICDCodes, setSelectedICDCodes] = useState([]);

  const currentPatient = {
    name: "Sarah Johnson",
    mrn: "MRN-2024-789456",
    dob: "03/15/1978",
    age: "48 years"
  };

  const patientAlerts = [
    {
      id: 1,
      severity: "critical",
      title: "Drug Interaction Alert",
      message: "Potential interaction between prescribed antibiotics and patient\'s current medications",
      actionRequired: "Review medication list before prescribing",
      dismissible: false
    },
    {
      id: 2,
      severity: "warning",
      title: "Allergy Alert",
      message: "Patient has documented Penicillin allergy",
      actionRequired: "Verify alternative antibiotics",
      dismissible: true
    }
  ];

  const documentationTemplates = [
    {
      id: 1,
      name: "SOAP Note",
      description: "Subjective, Objective, Assessment, Plan format for general dental documentation",
      icon: "FileText",
      specialty: "General",
      estimatedTime: "10-15 min"
    },
    {
      id: 2,
      name: "Progress Note",
      description: "Daily progress documentation for ongoing treatment",
      icon: "TrendingUp",
      specialty: "General",
      estimatedTime: "5-10 min"
    },
    {
      id: 3,
      name: "Comprehensive Exam",
      description: "Complete oral examination and periodontal charting documentation",
      icon: "Stethoscope",
      specialty: "General",
      estimatedTime: "20-30 min"
    },
    {
      id: 4,
      name: "Procedure Note",
      description: "Documentation for dental procedures and interventions",
      icon: "Activity",
      specialty: "Procedural",
      estimatedTime: "10-15 min"
    },
    {
      id: 5,
      name: "Treatment Completion Summary",
      description: "Comprehensive treatment completion documentation and home care instructions",
      icon: "LogOut",
      specialty: "General",
      estimatedTime: "15-20 min"
    },
    {
      id: 6,
      name: "Specialist Consultation Note",
      description: "Specialist consultation documentation and recommendations",
      icon: "Users",
      specialty: "Specialty",
      estimatedTime: "15-20 min"
    }
  ];

  const patientReferenceData = {
    vitals: [
      { label: "Blood Pressure", value: "128/82 mmHg", status: "normal", icon: "Activity" },
      { label: "Heart Rate", value: "76 bpm", status: "normal", icon: "Heart" },
      { label: "Temperature", value: "98.6°F", status: "normal", icon: "Thermometer" },
      { label: "Respiratory Rate", value: "16 /min", status: "normal", icon: "Wind" },
      { label: "O2 Saturation", value: "98%", status: "normal", icon: "Droplet" },
      { label: "Weight", value: "165 lbs", status: "normal", icon: "Scale" }
    ],
    medications: [
      {
        name: "Amoxicillin 500mg",
        dosage: "Three times daily, oral",
        prescribedDate: "01/05/2026",
        status: "Active"
      },
      {
        name: "Ibuprofen 600mg",
        dosage: "As needed for pain",
        prescribedDate: "12/20/2025",
        status: "Active"
      },
      {
        name: "Chlorhexidine Rinse 0.12%",
        dosage: "Twice daily, oral rinse",
        prescribedDate: "11/15/2025",
        status: "Active"
      }
    ],
    allergies: [
      {
        allergen: "Penicillin",
        reaction: "Severe rash, difficulty breathing",
        severity: "High"
      },
      {
        allergen: "Latex",
        reaction: "Contact dermatitis",
        severity: "Moderate"
      }
    ],
    labs: [
      {
        test: "Bleeding Time",
        result: "3.5 min",
        date: "01/10/2026",
        flag: "Normal"
      },
      {
        test: "Glucose",
        result: "95 mg/dL",
        date: "01/10/2026",
        flag: "Normal"
      },
      {
        test: "INR",
        result: "1.0",
        date: "01/10/2026",
        flag: "Normal"
      },
      {
        test: "Platelet Count",
        result: "250 K/uL",
        date: "12/15/2025",
        flag: "Normal"
      }
    ]
  };

  const smartPhrases = [
    {
      id: 1,
      title: "Normal Oral Exam",
      content: "Oral examination reveals good oral hygiene with no signs of active decay. Gingival tissues are pink and firm with no bleeding on probing. Periodontal probing depths are within normal limits. No soft tissue abnormalities noted. Occlusion is stable.",
      category: "assessment",
      shortcut: ".noe",
      usageCount: 245
    },
    {
      id: 2,
      title: "Periodontal Maintenance Plan",
      content: "Continue periodontal maintenance every 3 months. Patient counseled on importance of proper brushing and flossing technique. Recommend electric toothbrush and interdental cleaners. Follow-up periodontal charting in 6 months. Patient verbalized understanding of treatment plan.",
      category: "plan",
      shortcut: ".pmp",
      usageCount: 189
    },
    {
      id: 3,
      title: "Caries Assessment",
      content: "Multiple carious lesions identified on clinical and radiographic examination. Tooth #14 shows deep caries approaching pulp. Recommend composite restoration for teeth #3, #14, and #19. Patient counseled on dietary modifications and fluoride use.",
      category: "assessment",
      shortcut: ".car",
      usageCount: 167
    },
    {
      id: 4,
      title: "Prescription Review",
      content: "Prescription list reviewed and verified with patient. All current prescriptions confirmed for accuracy including dosage, frequency, and route of administration. Patient demonstrates understanding of medication regimen. No discrepancies identified. Updated prescription list provided to patient.",
      category: "plan",
      shortcut: ".medrec",
      usageCount: 312
    },
    {
      id: 5,
      title: "Chief Complaint Template",
      content: "Patient presents today with chief complaint of [symptom]. Symptoms began [timeframe] ago and are described as [quality/characteristics]. Associated symptoms include [list]. Denies [relevant negatives]. No previous episodes. No recent trauma.",
      category: "history",
      shortcut: ".cc",
      usageCount: 428
    }
  ];

  const clinicalAlerts = [
    {
      id: 1,
      severity: "critical",
      title: "Drug-Drug Interaction",
      message: "Antibiotic and anticoagulant combination may increase bleeding risk",
      recommendation: "Consider alternative antibiotic therapy or adjust anticoagulant dosing with close monitoring",
      actionable: true
    },
    {
      id: 2,
      severity: "warning",
      title: "Medical History Alert",
      message: "Patient has history of infective endocarditis. Antibiotic prophylaxis required",
      recommendation: "Prescribe appropriate antibiotic prophylaxis before invasive dental procedures",
      actionable: true
    },
    {
      id: 3,
      severity: "info",
      title: "Preventive Care Due",
      message: "Patient is due for routine prophylaxis and oral cancer screening",
      recommendation: "Schedule hygiene appointment and comprehensive oral examination",
      actionable: false
    }
  ];

  const documentVersions = [
    {
      id: 1,
      title: "Current Draft",
      author: "Dr. Michael Chen",
      timestamp: "01/14/2026 5:15 PM",
      wordCount: 487,
      changesSummary: "Added assessment and plan sections with medication adjustments"
    },
    {
      id: 2,
      title: "Auto-save",
      author: "Dr. Michael Chen",
      timestamp: "01/14/2026 5:10 PM",
      wordCount: 423,
      changesSummary: "Completed physical examination findings"
    },
    {
      id: 3,
      title: "Initial Draft",
      author: "Dr. Michael Chen",
      timestamp: "01/14/2026 5:00 PM",
      wordCount: 156,
      changesSummary: "Created document with chief complaint and history of present illness"
    }
  ];

  const quickActions = [
    {
      id: 'order-lab',
      label: 'Order Lab',
      icon: 'FlaskConical',
      onClick: () => navigate('/lab-results'),
      shortcut: 'Ctrl+L'
    },
    {
      id: 'prescribe',
      label: 'Prescribe',
      icon: 'Pill',
      onClick: () => navigate('/medication-management'),
      shortcut: 'Ctrl+P'
    },
    {
      id: 'schedule',
      label: 'Schedule',
      icon: 'Calendar',
      onClick: () => navigate('/appointment-scheduling'),
      shortcut: 'Ctrl+S'
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: 'DollarSign',
      onClick: () => navigate('/billing-management'),
      shortcut: 'Ctrl+B'
    }
  ];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    let templateContent = '';
    
    if (template?.name === 'SOAP Note') {
      templateContent = `SUBJECTIVE:\n\nOBJECTIVE:\n\nASSESSMENT:\n\nPLAN:\n`;
    } else if (template?.name === 'Progress Note') {
      templateContent = `Date: ${new Date()?.toLocaleDateString()}\n\nInterval History:\n\nPhysical Examination:\n\nAssessment and Plan:\n`;
    } else if (template?.name === 'H&P (History & Physical)') {
      templateContent = `CHIEF COMPLAINT:\n\nHISTORY OF PRESENT ILLNESS:\n\nPAST MEDICAL HISTORY:\n\nMEDICATIONS:\n\nALLERGIES:\n\nSOCIAL HISTORY:\n\nFAMILY HISTORY:\n\nREVIEW OF SYSTEMS:\n\nPHYSICAL EXAMINATION:\n\nASSESSMENT:\n\nPLAN:\n`;
    }
    
    setDocumentContent(templateContent);
  };

  const handleVoiceInput = () => {
    setIsVoiceActive(!isVoiceActive);
    if (!isVoiceActive) {
      console.log('Voice input activated');
    } else {
      console.log('Voice input deactivated');
    }
  };

  const handlePhraseSelect = (phrase) => {
    setDocumentContent(prev => prev + '\n\n' + phrase?.content);
    setShowSmartPhrases(false);
  };

  const handleICDCodeSelect = (icd) => {
    const icdText = `[ICD-10: ${icd?.code} - ${icd?.description}]`;
    setDocumentContent(documentContent + '\n' + icdText);
    setSelectedICDCodes([...selectedICDCodes, icd]);
  };

  const handleAlertAction = (alertId, action) => {
    console.log(`Alert ${alertId} action: ${action}`);
  };

  const handleVersionRestore = (versionId) => {
    console.log(`Restoring version: ${versionId}`);
  };

  const handleSaveDocument = () => {
    console.log('Saving document...');
  };

  const handleSignDocument = () => {
    console.log('Signing document...');
  };

  const handleAlertClick = (alert) => {
    console.log('Alert clicked:', alert);
  };

  const handleSessionExpired = () => {
    navigate('/login');
  };

  const handleSessionExtended = () => {
    console.log('Session extended');
  };

  const handleInsertICDCode = (icd) => {
    const icdText = `[ICD-10: ${icd?.code} - ${icd?.description}]`;
    setDocumentContent(documentContent + '\n' + icdText);
    setSelectedICDCodes([...selectedICDCodes, icd]);
  };

  return (
    <div className="min-h-screen bg-background pt-16">
      <Sidebar 
        isCollapsed={isSidebarCollapsed} 
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
      />

      <div className="">
        <PatientContextBar 
          patient={currentPatient}
          alerts={patientAlerts}
          onAlertClick={handleAlertClick}
        />

        <main className="p-4 md:p-6 lg:p-8">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
                  <Icon name="FileText" size={32} className="text-primary" />
                  Treatment Documentation
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Create and manage dental treatment notes with smart templates and voice dictation
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="History"
                  iconPosition="left"
                  onClick={() => setShowVersionHistory(!showVersionHistory)}
                >
                  <span className="hidden sm:inline">Version History</span>
                  <span className="sm:hidden">History</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Hash"
                  iconPosition="left"
                  onClick={() => setShowICDLookup(!showICDLookup)}
                >
                  ICD Codes
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="MessageSquare"
                  iconPosition="left"
                  onClick={() => setShowSmartPhrases(!showSmartPhrases)}
                >
                  Smart Phrases
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName={showReferencePanel ? 'PanelRightClose' : 'PanelRightOpen'}
                  iconPosition="left"
                  onClick={() => setShowReferencePanel(!showReferencePanel)}
                >
                  <span className="hidden sm:inline">Reference Panel</span>
                  <span className="sm:hidden">Panel</span>
                </Button>
              </div>
            </div>

            {!selectedTemplate && (
              <DocumentTemplateSelector
                selectedTemplate={selectedTemplate}
                onTemplateSelect={handleTemplateSelect}
                templates={documentationTemplates}
              />
            )}

            {selectedTemplate && (
              <>
                <div className="bg-card rounded-lg border border-border p-4 mb-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                        <Icon name={selectedTemplate?.icon} size={20} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h2 className="text-base md:text-lg font-semibold text-foreground">{selectedTemplate?.name}</h2>
                        <p className="text-xs md:text-sm text-muted-foreground">{selectedTemplate?.specialty} • Est. {selectedTemplate?.estimatedTime}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="X"
                        iconPosition="left"
                        onClick={() => {
                          setSelectedTemplate(null);
                          setDocumentContent('');
                        }}
                        fullWidth
                        className="sm:w-auto"
                      >
                        Change Template
                      </Button>
                    </div>
                  </div>
                </div>

                <TreatmentDecisionSupport
                  patientFactors={{
                    age: currentPatient?.age,
                    medicalHistory: patientReferenceData?.allergies,
                    currentMedications: patientReferenceData?.medications
                  }}
                  recommendations={clinicalAlerts}
                />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
                  <div className={`${showReferencePanel ? 'lg:col-span-8' : 'lg:col-span-12'}`}>
                    <RichTextEditor
                      content={documentContent}
                      onChange={setDocumentContent}
                      onVoiceInput={handleVoiceInput}
                      isVoiceActive={isVoiceActive}
                    />

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-6">
                      <Button
                        variant="default"
                        size="default"
                        iconName="Save"
                        iconPosition="left"
                        onClick={handleSaveDocument}
                        fullWidth
                        className="sm:w-auto"
                      >
                        Save Draft
                      </Button>
                      <Button
                        variant="default"
                        size="default"
                        iconName="CheckCircle"
                        iconPosition="left"
                        onClick={handleSignDocument}
                        fullWidth
                        className="sm:w-auto"
                      >
                        Sign & Complete
                      </Button>
                      <Button
                        variant="outline"
                        size="default"
                        iconName="Printer"
                        iconPosition="left"
                        fullWidth
                        className="sm:w-auto"
                      >
                        Print
                      </Button>
                      <Button
                        variant="outline"
                        size="default"
                        iconName="Share2"
                        iconPosition="left"
                        fullWidth
                        className="sm:w-auto"
                      >
                        Share
                      </Button>
                    </div>
                  </div>

                  {showReferencePanel && (
                    <div className="lg:col-span-4">
                      <PatientReferencePanel patientData={patientReferenceData} />
                    </div>
                  )}
                </div>
              </>
            )}

            {showICDLookup && (
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-foreground">ICD-10 Code Lookup</h3>
                  <button
                    onClick={() => setShowICDLookup(false)}
                    className="text-muted-foreground hover:text-foreground transition-smooth"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
                <ICDCodeLookup
                  onSelect={handleICDCodeSelect}
                  selectedCodes={selectedICDCodes}
                  multiple
                />
                {selectedICDCodes?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Selected Codes:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedICDCodes?.map((icd, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-xs font-mono">
                          {icd?.code}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {showSmartPhrases && (
              <div className="fixed inset-0 z-[500] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
                <div className="w-full max-w-2xl max-h-[90vh] bg-card rounded-xl shadow-elevation-5 border border-border overflow-hidden">
                  <div className="flex items-center justify-between p-4 border-b border-border">
                    <h2 className="text-lg font-semibold text-foreground">Smart Phrases Library</h2>
                    <button
                      onClick={() => setShowSmartPhrases(false)}
                      className="flex items-center justify-center w-8 h-8 rounded hover:bg-muted transition-smooth"
                    >
                      <Icon name="X" size={20} />
                    </button>
                  </div>
                  <div className="h-[calc(90vh-80px)]">
                    <SmartPhrasesPanel
                      phrases={smartPhrases}
                      onPhraseSelect={handlePhraseSelect}
                    />
                  </div>
                </div>
              </div>
            )}

            {showVersionHistory && (
              <div className="fixed inset-0 z-[500] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
                <div className="w-full max-w-3xl bg-card rounded-xl shadow-elevation-5 border border-border overflow-hidden">
                  <div className="flex items-center justify-between p-4 border-b border-border">
                    <h2 className="text-lg font-semibold text-foreground">Document Version History</h2>
                    <button
                      onClick={() => setShowVersionHistory(false)}
                      className="flex items-center justify-center w-8 h-8 rounded hover:bg-muted transition-smooth"
                    >
                      <Icon name="X" size={20} />
                    </button>
                  </div>
                  <DocumentVersionHistory
                    versions={documentVersions}
                    onVersionRestore={handleVersionRestore}
                    onVersionCompare={(v1, v2) => console.log(`Comparing versions: ${v1} and ${v2}`)}
                  />
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      <QuickActions actions={quickActions} position="bottom-right" />
      <SessionManager
        sessionTimeout={900000}
        warningTime={120000}
        onSessionExpired={handleSessionExpired}
        onSessionExtended={handleSessionExtended}
      />
    </div>
  );
};

export default TreatmentDocumentation;